import { createReducer, on } from '@ngrx/store';
import { chunk, cloneDeep, omit, orderBy } from 'lodash';
import qs from 'qs';
import { Chat } from '@providers/api/chat/chat.request.interface';
import { assistantChatActions } from './chat.actions';
import {
  AssistantChatState,
  ChannelChatQuery,
  LocalChat,
  assistantChatState
} from './chat.state';

const MAX_QUERY_KEYS = 10;

export const assistantChatReducer = createReducer(
  assistantChatState,
  on(assistantChatActions.setChannelHasMessage, (state, payload) => {
    return {
      ...state,
      channelHasChat: payload.result
    };
  }),
  on(assistantChatActions.fetchChatHistory, (state) => ({
    ...state,
    channelHistoryQuery: {
      ...state.channelHistoryQuery,
      loading: true,
      error: undefined
    }
  })),
  on(assistantChatActions.reFetchChatHistory, (state) => ({
    ...state,
    channelHistoryQuery: {
      ...state.channelHistoryQuery,
      loading: true,
      error: undefined,
      entities: {},
      ids: [],
      idsByPage: {},
      pagination: {
        page: 0,
        totalItems: 0
      }
    }
  })),
  on(assistantChatActions.fetchChatHistorySuccess, (state, payload) => {
    const channelHistoryQuery = {
      ...state.channelHistoryQuery
    };
    channelHistoryQuery.loading = false;
    channelHistoryQuery.error = undefined;

    const entities = { ...state.channelHistoryQuery.entities };
    const idsByPage = { ...state.channelHistoryQuery.idsByPage };
    idsByPage[payload.pagination.page] = [];
    for (const item of payload.items) {
      idsByPage[payload.pagination.page].push(item.id);
      entities[item.id] = item;
    }
    const ids: Array<string> = [];
    for (const pageKey of Object.keys(idsByPage)) {
      ids.push(...idsByPage[pageKey]);
    }
    channelHistoryQuery.entities = entities;
    channelHistoryQuery.idsByPage = idsByPage;
    channelHistoryQuery.ids = ids;
    channelHistoryQuery.queryCount = channelHistoryQuery.queryCount + 1;
    channelHistoryQuery.pagination = {
      page: payload.pagination.page,
      totalItems: payload.pagination.totalItems
    };

    return {
      ...state,
      channelHistoryQuery
    };
  }),
  on(assistantChatActions.fetchChatHistoryFailed, (state) => ({
    ...state,
    channelHistoryQuery: {
      ...state.channelHistoryQuery,
      queryCount: state.channelHistoryQuery.queryCount + 1,
      error: undefined,
      loading: false
    }
  })),
  on(assistantChatActions.setNewMessage, (state, { message }) => ({
    ...state,
    newMessage: message
  })),
  on(assistantChatActions.setCurrentChannelId, (state, payload) => {
    const query: ChannelChatQuery['query'] = {
      ...state.channelChat.baseQuery
    };
    if (payload.channelId) {
      query.channelId = [payload.channelId];
    }
    return {
      ...state,
      loading: {
        ...state.loading,
        checkChannelHasChat: true
      },
      currentChannelId: payload.channelId
    };
  }),
  on(assistantChatActions.setChannelChatQuery, (state, payload) => {
    const query: AssistantChatState['channelChat']['currentQuery'] = {
      ...payload.query
    };

    const queryKeys = qs.stringify(query, {
      arrayFormat: 'repeat',
      skipNulls: true
    });
    return {
      ...state,
      channelChat: {
        ...state.channelChat,
        currentQuery: query,
        currentQueryKey: queryKeys
      }
    };
  }),
  on(assistantChatActions.fetchChannelChats, (state, payload) => {
    const channelId = state.currentChannelId;
    let queryChannelIds = [...state.channelChat.queryChannelIds];
    if (channelId) {
      let channelQueries: AssistantChatState['channelChat']['queries'] =
        cloneDeep({
          ...state.channelChat.queries,
          [channelId]: {
            ...(state.channelChat.queries[channelId] ?? {})
          }
        });

      const currentQueryKey = state.channelChat.currentQueryKey;
      queryChannelIds.push(channelId);
      if (queryChannelIds.length > MAX_QUERY_KEYS) {
        for (let i = 0; i < queryChannelIds.length; i++) {
          const willRemovedChannelId = queryChannelIds[i];
          if (willRemovedChannelId !== state.currentChannelId) {
            queryChannelIds.splice(i, 1);

            const willRemovedChannelQuery = channelQueries[
              willRemovedChannelId
            ] as Record<string, ChannelChatQuery> | undefined;
            if (willRemovedChannelQuery) {
              channelQueries = omit(channelQueries, [willRemovedChannelId]);
            }
            break;
          }
        }
      }

      const channelQuery: ChannelChatQuery = cloneDeep({
        entities: {},
        error: undefined,
        ids: [],
        idsByPage: {},
        loading: false,
        pagination: {
          page: -1,
          totalItems: 0
        },
        query: {
          ...state.channelChat.baseQuery,
          ...state.channelChat.currentQuery
        },
        ...((channelQueries[channelId][currentQueryKey] as
          | ChannelChatQuery
          | undefined) ?? {})
      });
      if (payload.reset) {
        channelQuery.entities = {};
        channelQuery.ids = [];
        channelQuery.idsByPage = {};
        channelQuery.pagination = {
          page: payload.page,
          totalItems: 0
        };
      }
      channelQuery.loading = true;
      channelQuery.error = undefined;

      channelQueries[channelId][currentQueryKey] = channelQuery;
      return {
        ...state,
        channelChat: {
          ...state.channelChat,
          queryChannelIds,
          queries: channelQueries
        }
      };
    }
    return {
      ...state,
      channelChat: {
        ...state.channelChat,
        queryChannelIds
      }
    };
  }),
  on(assistantChatActions.fetchChannelChatsSuccess, (state, payload) => {
    if (payload.channelId) {
      let channelQueries: AssistantChatState['channelChat']['queries'] = {
        ...state.channelChat.queries,
        [payload.channelId]: {
          ...(state.channelChat.queries[payload.channelId] ?? {})
        }
      };

      const currentQueryKey = state.channelChat.currentQueryKey;
      const channelQuery: ChannelChatQuery = {
        entities: {},
        error: undefined,
        ids: [],
        idsByPage: {},
        loading: false,
        pagination: {
          page: -1,
          totalItems: 0
        },
        query: {
          ...state.channelChat.baseQuery,
          ...state.channelChat.currentQuery
        },
        ...((channelQueries[payload.channelId][currentQueryKey] as
          | ChannelChatQuery
          | undefined) ?? {})
      };
      channelQuery.loading = false;
      channelQuery.error = undefined;

      const channelQueryKeys = Object.keys(
        channelQueries[payload.channelId] ?? {}
      );
      if (channelQueryKeys.length > MAX_QUERY_KEYS) {
        const index = channelQueryKeys.findIndex(
          (item) =>
            item !== state.channelChat.mainQueryKey && item !== currentQueryKey
        );
        if (index >= 0) {
          channelQueries = {
            ...channelQueries,
            [payload.channelId]: omit(channelQueries[payload.channelId], [
              channelQueryKeys[index]
            ])
          };
        }
      }

      const entities = cloneDeep(channelQuery.entities);
      const idsByPage = cloneDeep(channelQuery.idsByPage);

      for (const entity of payload.items) {
        entities[entity.id] = entity;

        const currentPageIds = idsByPage[payload.page] as
          | Array<string>
          | undefined;
        if (currentPageIds) {
          const prevIndex = idsByPage[payload.page].findIndex(
            (idItem) => idItem === entity.id
          );
          if (prevIndex >= 0) {
            idsByPage[payload.page][prevIndex] = entity.id;
          } else {
            idsByPage[payload.page].push(entity.id);
          }
        } else {
          idsByPage[payload.page] = [entity.id];
        }
      }
      const ids: Array<string> = [];
      for (const idsPerPage of Object.keys(idsByPage).map(
        (page) => idsByPage[page]
      )) {
        ids.push(...idsPerPage);
      }
      channelQuery.ids = ids;
      channelQuery.entities = entities;
      channelQuery.idsByPage = idsByPage;

      channelQuery.pagination = {
        page: payload.page,
        totalItems: payload.totalItems
      };

      channelQueries[payload.channelId][currentQueryKey] = channelQuery;
      return {
        ...state,
        channelChat: {
          ...state.channelChat,
          queries: channelQueries
        }
      };
    }
    return state;
  }),
  on(assistantChatActions.fetchChannelChatsFailed, (state, payload) => {
    if (payload.channelId) {
      const channelQueries: AssistantChatState['channelChat']['queries'] = {
        ...state.channelChat.queries,
        [payload.channelId]: {
          ...(state.channelChat.queries[payload.channelId] ?? {})
        }
      };

      const currentQueryKey = state.channelChat.currentQueryKey;

      const channelQuery: ChannelChatQuery = {
        entities: {},
        error: undefined,
        ids: [],
        idsByPage: {},
        loading: false,
        pagination: {
          page: -1,
          totalItems: 0
        },
        query: {
          ...state.channelChat.baseQuery,
          ...state.channelChat.currentQuery
        },
        ...((channelQueries[payload.channelId][currentQueryKey] as
          | ChannelChatQuery
          | undefined) ?? {})
      };
      channelQuery.loading = false;
      channelQuery.error = payload.error;

      channelQueries[payload.channelId][currentQueryKey] = channelQuery;
      return {
        ...state,
        channelChat: {
          ...state.channelChat,
          queries: channelQueries
        }
      };
    }
    return state;
  }),
  on(assistantChatActions.updateLocalChat, (state, payload) => {
    const addedOrRemoved =
      (Array.isArray(payload.added) && payload.added.length > 0) ||
      (Array.isArray(payload.removed) && payload.removed.length > 0);
    if (state.currentChannelId && addedOrRemoved) {
      let localMessage = {
        ...state.channelChat.localChat
      };

      let messages: Array<LocalChat> = [
        ...(state.channelChat.localChat[state.currentChannelId] ?? [])
      ];

      if (Array.isArray(payload.added) && payload.added.length > 0) {
        for (const msg of payload.added) {
          const storedAtIndex = messages.findIndex(
            (item) => item.id === msg.id
          );
          if (storedAtIndex >= 0) {
            messages[storedAtIndex] = msg;
          } else {
            messages.push(msg);
          }
        }
        messages = orderBy(messages, ['createdAt'], ['desc']);
      }
      const removed = payload.removed;
      if (Array.isArray(removed) && removed.length > 0) {
        messages = messages.filter(
          (item) => !removed.some((removedItem) => removedItem.id === item.id)
        );
      }

      const currentChannelLocalMessage = localMessage[
        state.currentChannelId
      ] as Array<LocalChat> | undefined;
      if (messages.length === 0 && currentChannelLocalMessage) {
        localMessage = omit(localMessage, [state.currentChannelId]);
      } else {
        localMessage[state.currentChannelId] = messages;
      }
      return {
        ...state,
        channelChat: {
          ...state.channelChat,
          localChat: localMessage
        }
      };
    }
    return state;
  }),
  on(assistantChatActions.addIncomingChannelChatMessage, (state, payload) => {
    if (state.currentChannelId) {
      const channelQueries = state.channelChat.queries[
        payload.item.channelId
      ] as AssistantChatState['channelChat']['queries'][''] | undefined;
      if (channelQueries) {
        const query = channelQueries[state.channelChat.mainQueryKey] as
          | ChannelChatQuery
          | undefined;
        if (query) {
          let cQuery: ChannelChatQuery = cloneDeep(query);
          let totalItems = cQuery.pagination.totalItems;

          const entities = Object.values(cQuery.entities);
          const entityStoredIndex = entities.findIndex(
            (item) => item.id === payload.item.id
          );
          if (entityStoredIndex >= 0) {
            cQuery.entities[payload.item.id] = payload.item;
          } else {
            const newEntities = orderBy(
              [...entities, payload.item],
              ['createdAt'],
              ['desc']
            );
            const splittedEntities = chunk(
              newEntities,
              state.channelChat.queryLimit
            );
            const idsByPage: ChannelChatQuery['idsByPage'] = {};
            const ids: ChannelChatQuery['ids'] = [];
            for (let i = 0; i < splittedEntities.length; i++) {
              const entities = splittedEntities[i];

              const entitiyIds = entities.map((item) => item.id);
              idsByPage[i] = entitiyIds;
              ids.push(...entitiyIds);
            }
            if (
              query.pagination.page === -1 ||
              (query.pagination.page >= 0 &&
                Array.isArray(query.idsByPage[0]) &&
                query.idsByPage[0].length + 1 > state.channelChat.queryLimit)
            ) {
              cQuery.pagination.page += 1;
            }
            totalItems = newEntities.length;
            cQuery.ids = ids;
            cQuery.entities = {
              ...cQuery.entities,
              [payload.item.id]: payload.item
            };
            cQuery.idsByPage = idsByPage;
          }
          cQuery.pagination.totalItems = totalItems;
          return {
            ...state,
            channelChat: {
              ...state.channelChat,
              queries: {
                ...state.channelChat.queries,
                [payload.item.channelId]: {
                  ...state.channelChat.queries[payload.item.channelId],
                  [state.channelChat.currentQueryKey]: cQuery
                }
              }
            }
          };
        }
      }
    }
    return state;
  }),
  on(assistantChatActions.checkChannelHasChat, (state) => {
    return {
      ...state,
      loading: {
        ...state.loading,
        checkChannelHasChat: true
      }
    };
  }),
  on(assistantChatActions.checkChannelHasChatSuccess, (state, payload) => {
    return {
      ...state,
      channelHasChat: payload.result,
      loading: {
        ...state.loading,
        checkChannelHasChat: false
      }
    };
  }),
  on(assistantChatActions.checkChannelHasChatFailed, (state) => {
    return {
      ...state,
      channelHasChat: false,
      loading: {
        ...state.loading,
        checkChannelHasChat: false
      }
    };
  }),
  on(assistantChatActions.setFavourite, (state, payload) => ({
    ...state,
    favourite: payload.favourite
  })),
  on(assistantChatActions.checkChannel, (state) => ({
    ...state,
    checkChannelQuery: {
      ...state.checkChannelQuery,
      loading: true,
      error: undefined,
      queryCount: state.checkChannelQuery.queryCount + 1
    }
  })),
  on(assistantChatActions.checkChannelSuccess, (state, payload) => ({
    ...state,
    checkChannelQuery: {
      ...state.checkChannelQuery,
      result: payload.result,
      loading: false,
      error: undefined
    }
  })),
  on(assistantChatActions.checkChannelFailed, (state, payload) => ({
    ...state,
    checkChannelQuery: {
      ...state.checkChannelQuery,
      result: false,
      loading: false,
      error: payload.error
    }
  })),
  on(assistantChatActions.resetAllUserData, () => ({
    ...assistantChatState
  })),
  on(assistantChatActions.setAIModel, (state, payload) => ({
    ...state,
    AIModel: payload.model
  })),
  on(assistantChatActions.setSearchText, (state, payload) => ({
    ...state,
    searchText: payload.text
  })),
  on(assistantChatActions.setSkipOnboarding, (state, payload) => ({
    ...state,
    allowedToSkipOnboarding: payload.skip
  })),
  on(assistantChatActions.setEditedChat, (state, payload) => ({
    ...state,
    editedChat: payload.chat
  })),
  on(assistantChatActions.setProcessGenerateMedia, (state, payload) => ({
    ...state,
    processGenerateMedia: payload.processGenerateMedia
  })),
  on(assistantChatActions.setStatusFile, (state, payload) => ({
    ...state,
    statusFile: {
      loading: payload.loading,
      message: payload.message,
      status: payload.status,
      fileAttachmentCount: payload.fileAttachmentCount
    }
  })),
  on(assistantChatActions.setSelectedFile, (state, payload) => {
    const mergeData = [...state.selectedFile, ...payload.selectedFile];
    const updateData = mergeData.filter(
      (value, index, self) => index === self.findIndex((t) => t.id === value.id)
    );
    return {
      ...state,
      selectedFile: updateData
    };
  }),
  on(assistantChatActions.resetAllSelectedFile, (state) => ({
    ...state,
    selectedFile: []
  })),
  on(assistantChatActions.updateChatSuccess, (state, payload) => {
    const channelQueries = (state.channelChat.queries[payload.channelId] as
      | Record<string, ChannelChatQuery>
      | undefined)
      ? cloneDeep(state.channelChat.queries[payload.channelId])
      : undefined;
    if (channelQueries && payload.chat?.id) {
      if (Object.keys(channelQueries).length > 0) {
        for (const queryKey in channelQueries) {
          const query = channelQueries[queryKey] as
            | ChannelChatQuery
            | undefined;
          if (query) {
            const chat = query.entities[payload.chat.id] as Chat | undefined;
            if (chat) {
              channelQueries[queryKey].entities[payload.chat.id] = payload.chat;
            }
          }
        }
        return {
          ...state,
          channelChat: {
            ...state.channelChat,
            queries: {
              ...state.channelChat.queries,
              [payload.channelId]: channelQueries
            }
          }
        };
      }
    }
    return state;
  }),
  on(assistantChatActions.setGenerateMediaChannelId, (state, payload) => ({
    ...state,
    generateMediaChannelId: payload.channelId
  })),
  on(assistantChatActions.setNeedRefetchChat, (state, payload) => ({
    ...state,
    isNeedRefetchChat: payload.isNeedRefetchChat
  }))
);
